/**
 * IssueDescription — renders a structured description parsed from the
 * compiled format: "Selected Issues:\n• item\n\nCustomer Description:\ntext"
 * Falls back to raw text if the format is not detected.
 */
const IssueDescription = ({ description, compact = false }) => {
  if (!description) return null;

  // Detect if warranty is expired from backend-appended marker
  const isExpired = description.includes('[WARRANTY STATUS: EXPIRED]');
  
  // Clean description by removing the marker
  const cleanDescription = description.replace(/\s*\[WARRANTY STATUS: EXPIRED\]\s*$/, '').trim();

  // Parse structured format
  let selectedIssues = [];
  let customerDesc = '';

  const issuesMatch = cleanDescription.match(/Selected Issues:\s*\n?([\s\S]*?)(?:\n\n|$)/);
  const descMatch = cleanDescription.match(/(?:Customer|Caller) Description:\s*\n?([\s\S]*?)$/);

  if (issuesMatch) {
    selectedIssues = issuesMatch[1]
      .split('\n')
      .map(s => s.replace(/^[•\-\*]\s*/, '').trim())
      .filter(Boolean);
  }
  if (descMatch) {
    customerDesc = descMatch[1].trim();
  }

  // Fallback: no structured format detected
  if (!selectedIssues.length && !customerDesc) {
    return (
      <div className="space-y-3">
        {isExpired && (
          <div className="flex items-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-55 border border-red-200 rounded-full text-xs font-black text-red-700 uppercase tracking-wider shadow-sm bg-red-50/80">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              Warranty Status: Expired
            </span>
          </div>
        )}
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{cleanDescription}</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5 items-center">
          {isExpired && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded-full text-[10px] font-bold text-red-700 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              Expired Warranty
            </span>
          )}
          {selectedIssues.length > 0 && (
            selectedIssues.map((issue, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-[11px] font-bold text-amber-800"
              >
                <span className="w-1 h-1 rounded-full bg-amber-400" />
                {issue}
              </span>
            ))
          )}
        </div>
        {customerDesc && (
          <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">{customerDesc}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isExpired && (
        <div className="flex items-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-55 border border-red-200 rounded-full text-xs font-black text-red-700 uppercase tracking-wider shadow-sm bg-red-50/80">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Warranty Status: Expired
          </span>
        </div>
      )}

      {/* Selected Issues as tags */}
      {selectedIssues.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Reported Issues</p>
          <div className="flex flex-wrap gap-2">
            {selectedIssues.map((issue, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-xs font-bold text-amber-800"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                {issue}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Customer Description */}
      {customerDesc && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Customer Description</p>
          <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{customerDesc}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IssueDescription;
